<script setup>
import { onMounted, computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useLeadActivitiesStore } from 'dashboard/stores/leadActivities';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NewTaskDialog from 'dashboard/components-next/Crm/Tasks/NewTaskDialog.vue';
import LeadDrawer from './LeadDrawer.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const store = useLeadActivitiesStore();
const router = useRouter();
const newTaskDialogRef = ref(null);
const drawerRef = ref(null);

// Detalhar a tarefa = abrir o lead vinculado (drawer com resumo, atividades,
// follow-up). Fallback pra conversa quando não há lead_id no payload.
function openDetail(activity) {
  if (activity.lead_id) {
    drawerRef.value?.open(activity.lead_id);
  } else {
    openLead(activity);
  }
}

// Loading do fetch inicial - evita exibir o empty state ("Nenhuma tarefa")
// enquanto a requisição ainda está em andamento.
const isLoading = ref(true);

onMounted(async () => {
  try {
    await store.fetchMyPending();
  } finally {
    isLoading.value = false;
  }
});

const GROUP_LABELS = {
  overdue: { label: 'Atrasadas', icon: 'i-lucide-alert-triangle', color: 'text-n-ruby-11' },
  today:   { label: 'Hoje',      icon: 'i-lucide-calendar-clock', color: 'text-n-amber-11' },
  next7d:  { label: 'Próximos 7 dias', icon: 'i-lucide-calendar-days', color: 'text-n-brand' },
  later:   { label: 'Depois',    icon: 'i-lucide-calendar', color: 'text-n-slate-11' },
};

const KIND_META = {
  call:     { icon: 'i-lucide-phone',          label: 'Ligação' },
  email:    { icon: 'i-lucide-mail',           label: 'E-mail' },
  whatsapp: { icon: 'i-lucide-message-circle', label: 'WhatsApp' },
  visit:    { icon: 'i-lucide-map-pin',        label: 'Visita' },
  meeting:  { icon: 'i-lucide-calendar',       label: 'Reunião' },
  note:     { icon: 'i-lucide-sticky-note',    label: 'Nota' },
};

const grouped = computed(() => {
  const now = Date.now();
  const groups = { overdue: [], today: [], next7d: [], later: [] };
  for (const a of store.myPending) {
    const due = new Date(a.due_at).getTime();
    if (due < now - 86400000) groups.overdue.push(a);
    else if (due - now < 86400000) groups.today.push(a);
    else if (due - now < 7 * 86400000) groups.next7d.push(a);
    else groups.later.push(a);
  }
  return groups;
});

const totalPending = computed(() => store.myPending?.length || 0);

function formatDueAt(iso) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function kindMeta(kind) {
  return KIND_META[kind] || { icon: 'i-lucide-circle', label: kind };
}

// Checkbox por item - completa a tarefa rápido e remove da lista
// (otimização: atualizar `myPending` localmente em vez de re-fetch).
async function toggleComplete(activity) {
  try {
    await store.complete(activity.lead_id, activity.id, 'success', null);
    store.myPending = store.myPending.filter(a => a.id !== activity.id);
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao concluir.');
  }
}

function openLead(activity) {
  if (activity.conversation_id) {
    router.push(`/app/accounts/${window.WOOT?.accountId || ''}/conversations/${activity.conversation_id}`);
  }
}

function openNewTask() {
  newTaskDialogRef.value?.open();
}

function onTaskCreated() {
  store.fetchMyPending();
}
</script>

<template>
  <!-- Mesmo gabarito das outras views top-level já no padrão (PipelineKanban,
       Relatórios): scroll container com `bg-n-surface-1` + conteúdo com
       `max-w-[88rem] mx-auto`. -->
  <div class="w-full h-full overflow-y-auto bg-n-surface-1 font-inter">
    <div class="max-w-[88rem] mx-auto px-6 py-6 flex flex-col gap-4">

      <!-- Header no padrão BaseSettingsHeader (mesma estrutura/tipografia das
           telas de Configurações): título + descrição empilhados, e a faixa
           de ações (contador + botão) numa linha SEPARADA abaixo. -->
      <div>
        <div class="flex flex-col items-start w-full">
          <div class="flex items-center justify-between w-full gap-4 min-h-8 mb-2">
            <h1 class="text-heading-1 text-n-slate-12">Minhas tarefas</h1>
          </div>
          <div class="flex flex-col w-full gap-1.5 text-n-slate-11">
            <p class="mb-0 line-clamp-5 sm:line-clamp-none max-w-3xl text-body-main">
              Atividades de follow-up agendadas para você. Conclua os passos no ritmo planejado para manter os leads aquecidos.
            </p>
          </div>
        </div>
        <div class="gap-3 flex justify-between sm:mt-4 min-w-0">
          <div class="flex items-center gap-3 min-w-0">
            <span
              v-if="totalPending"
              class="text-body-small px-2 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-11"
            >{{ totalPending }} pendente(s)</span>
          </div>
          <div class="flex items-center gap-3 min-w-0">
            <NextButton
              icon="i-lucide-plus"
              size="sm"
              label="Nova tarefa"
              @click="openNewTask"
            />
          </div>
        </div>
      </div>

      <!-- Loading inicial padrão da plataforma. Some assim que o fetch resolve;
           só então decidimos entre empty state e lista. -->
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-24"
      >
        <Spinner />
      </div>

      <!-- Empty state SIMPLES, em fluxo normal: sem ghost-cards, sem camada
           `absolute`, sem dependência de altura/cadeia. Não há como sobrepor
           nada - correção definitiva (o EmptyStateLayout era frágil aqui). -->
      <div
        v-else-if="!totalPending"
        class="flex flex-col items-center justify-center text-center gap-4 py-24"
      >
        <span class="size-14 rounded-2xl bg-n-alpha-2 flex items-center justify-center">
          <i class="i-lucide-calendar-check size-7 text-n-slate-11" />
        </span>
        <div>
          <h2 class="text-heading-2 text-n-slate-12 mb-1.5">
            Nenhuma tarefa pendente
          </h2>
          <p class="text-body-main text-n-slate-11 max-w-md mx-auto mb-0">
            Você está em dia. Tarefas geradas pelos modelos de follow-up dos leads sob sua responsabilidade aparecem aqui - com data, contato e tipo de toque.
          </p>
        </div>
        <NextButton
          icon="i-lucide-plus"
          label="Criar primeira tarefa"
          @click="openNewTask"
        />
      </div>

      <!-- Lista agrupada por janela temporal - flutuante, sem card wrapper.
           Cada grupo é uma seção fina com header sticky. -->
      <div v-else class="flex flex-col gap-6">
        <section
          v-for="(list, key) in grouped"
          v-show="list.length"
          :key="key"
          class="flex flex-col gap-2"
        >
          <header class="flex items-center gap-2 pb-1 border-b border-n-weak">
            <i :class="[GROUP_LABELS[key].icon, 'size-4', GROUP_LABELS[key].color]" />
            <h2 class="text-heading-3 mb-0" :class="GROUP_LABELS[key].color">
              {{ GROUP_LABELS[key].label }}
            </h2>
            <span class="text-body-small text-n-slate-11">· {{ list.length }}</span>
          </header>

          <ol class="flex flex-col">
            <li
              v-for="a in list"
              :key="a.id"
              class="group px-3 py-3 flex items-center gap-4 border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 transition-colors rounded-md"
            >
              <!-- Tick pra concluir rápido. O ✓ aparece no hover do card e
                   fica sólido (n-brand) ao passar o mouse no próprio botão. -->
              <button
                type="button"
                v-tooltip="'Marcar como concluída'"
                class="size-5 rounded-md border-2 border-n-weak hover:border-n-brand hover:bg-n-brand/10 transition-colors flex items-center justify-center shrink-0"
                @click.stop="toggleComplete(a)"
              >
                <i
                  class="i-lucide-check size-3.5 text-n-slate-9 opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </button>

              <span class="size-9 rounded-full bg-n-alpha-2 flex items-center justify-center flex-shrink-0">
                <i :class="kindMeta(a.kind).icon" class="size-4 text-n-slate-12" />
              </span>

              <div class="flex-1 min-w-0 cursor-pointer" @click="openDetail(a)">
                <div class="flex items-center gap-2 min-w-0">
                  <p class="text-body-main font-medium text-n-slate-12 truncate mb-0">{{ a.title }}</p>
                  <span class="text-label-small px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11 uppercase tracking-wide">
                    {{ kindMeta(a.kind).label }}
                  </span>
                </div>
                <div class="flex items-center gap-4 mt-1 text-body-small text-n-slate-11">
                  <span v-if="a.lead_name" class="inline-flex items-center gap-1 truncate">
                    <i class="i-lucide-user size-3" />
                    {{ a.lead_name }}
                  </span>
                  <span class="inline-flex items-center gap-1">
                    <i class="i-lucide-clock size-3" />
                    {{ formatDueAt(a.due_at) }}
                  </span>
                  <span
                    v-if="a.from_template"
                    class="inline-flex items-center gap-1 text-n-slate-10"
                  >
                    <i class="i-lucide-list-checks size-3" />
                    {{ a.template_name }} · passo {{ (a.template_step_position || 0) + 1 }}
                  </span>
                </div>
              </div>
            </li>
          </ol>
        </section>
      </div>
    </div>

    <NewTaskDialog ref="newTaskDialogRef" @created="onTaskCreated" />
    <LeadDrawer ref="drawerRef" @lead-updated="store.fetchMyPending()" />
  </div>
</template>
